import React, { PropsWithChildren, useState, useEffect } from 'react';
import ScribeContext, { ScribeStatus } from './ScribeContext';

const ScribeContextProvider = (props: PropsWithChildren): JSX.Element => {
  const [isScribing, setIsScribing] = useState<ScribeStatus>(ScribeStatus.STOPPED);
  const [timer, setTimer] = useState(0);
  const [consentModalOpen, setConsentModalOpen] = useState(false);

  // Timer effect - runs only when actively scribing
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isScribing === ScribeStatus.STARTED) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }
    // Only reset timer when stopping, not when pausing
    // This allows the timer to persist through pause/resume cycles
    if (isScribing === ScribeStatus.STOPPED) {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [isScribing]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartScribe = () => {
    setConsentModalOpen(true);
  };

  const handleResumeScribe = () => {
    setIsScribing(ScribeStatus.STARTED);
  };

  const handleConsentConfirm = () => {
    setConsentModalOpen(false);
    setIsScribing(ScribeStatus.STARTED);
  };

  const handleConsentCancel = () => {
    setConsentModalOpen(false);
  };

  const handleStopScribe = () => {
    setIsScribing(ScribeStatus.STOPPED);
  };

  const handlePause = () => {
    setIsScribing(ScribeStatus.PAUSED);
  };

  return (
    <ScribeContext.Provider
      value={{
        isScribing,
        timer,
        consentModalOpen,
        setIsScribing,
        setTimer,
        setConsentModalOpen,
        handleStartScribe,
        handleResumeScribe,
        handleConsentConfirm,
        handleConsentCancel,
        handleStopScribe,
        handlePause,
        formatTime,
      }}>
      {props.children}
    </ScribeContext.Provider>
  );
};

export default ScribeContextProvider;
